import { Controller } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import {
  InputText,
  InputSize,
  DateInput,
  DateInputSize,
  Autocomplete,
  SelectSize,
  Select,
  SelectType,
  Textarea,
  formatPhone,
  PermissionEnum,
  InputNumber,
  IconClose,
  ButtonSecondary,
  IconExpand,
  ButtonSize,
} from '@ui/src';
import { useAppSelector } from '@store';
import { ChangeEvent, useEffect, useState } from 'react';
import InfoRow from '../info-row';
import { NewApplicationStatus } from '../../../hoc/new-application/types';
import StepOneAdditionalBlock from './step-one-additional-block';
import { IndividualApplicationStepOneTypes } from '../types';
import StepPartBlock from '../corporate/parts/step-part-block';
import GroupInformation from './parts/group-information';
import SourceOfIncome from './parts/source-of-income';
import SourceAndSizeOfWealth from './parts/source-and-size-of-wealth';
import AffilatedCompanies from './parts/affilated-companies';

import cn from '../new-application.module.css';

let emailsTimeout: NodeJS.Timeout;

const NewApplicationInfoStepOne = ({
  control,
  errors,
  status,
  companyOfficial,
  isEditable,
  clientActivity,
  sourceOfFundsIds,
  hasSumSubId,
  applicationScheme,
  clearErrors,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  activePermissions,
  regulatedCompanyScheme,
  questionnarieFields,
  sourceOfWealth,
  baseCurrency,
  questionnarieSetValue,
  sourceOfIncome,
  sourceOfWealthForIndividual,
  groupStructurChartFile,
  cofRegulatedCompanyScheme,
  affilatedCompaniesValues,
  setAffilatedCompaniesValues,
  affilatedCompaniesIsLoading,
  affilatedCompaniesIsFetching,
  deleteAffilatedCompanyInProgress,
  onDeleteAffilatedCompany,
  selectedAffilatedCompanyId,
  uboPercentage,
}: IndividualApplicationStepOneTypes) => {
  const [additionalEmails, setAdditionalEmails] = useState<
    { id: string; email: string }[]
  >([
    {
      id: `email-${uuid()}`,
      email: '',
    },
  ]);
  const {
    countries,
    sourcesOfFunds,
    clientsActivities,
    deliveryChannelsIndividual,
  } = useAppSelector((state) => state.customersDictionaries);

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const otherSourceOfFunds = sourcesOfFunds?.find(
    (item) => item.name === 'Other',
  );

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    clearTimeout(emailsTimeout);

    const nextEmails = additionalEmails?.map((item) => {
      const result = { ...item };

      if (e.target.name === `email-${result.id}`) {
        result.email = e.target.value;
      }

      return result;
    });

    emailsTimeout = setTimeout(() => {
      questionnarieSetValue(
        'emails',
        nextEmails?.filter((item) => item.email)?.map((item) => item.email),
      );
    }, 700);

    setAdditionalEmails(nextEmails);
  };

  const onDeleteAdditionalEmails = (id: string) => {
    const nextEmails = additionalEmails?.filter((item) => item.id !== id);

    setAdditionalEmails(nextEmails);
    questionnarieSetValue(
      'emails',
      nextEmails?.filter((item) => item.email)?.map((item) => item.email),
    );
  };

  useEffect(() => {
    if (questionnarieFields?.emails?.length) {
      setAdditionalEmails(
        questionnarieFields?.emails?.map((item) => ({
          id: `email-${uuid()}`,
          email: item,
        })),
      );
    }
  }, []);

  const employmentContent = (
    <>
      <InfoRow title="Name of employer" id="nameOfEmployer" lessPadding>
        <Controller
          control={control}
          name="nameOfEmployer"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="nameOfEmployer"
              placeholder="Enter name of employer"
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['nameOfEmployer']);
              }}
              error={errors?.nameOfEmployer?.message}
              size={InputSize.SMALL}
              className="borderless"
              tabIndex={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          )}
        />
      </InfoRow>
      {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
        <InfoRow
          title="Nature of business activity"
          id="natureOfBusinessActivity"
          lessPadding
        >
          <Controller
            control={questionnarieControl}
            name="natureOfBusinessActivity"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <InputText
                name={name}
                placeholder="Enter description..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  questionnarieClearErrors(['natureOfBusinessActivity']);
                }}
                size={InputSize.SMALL}
                error={questionnarieErrors?.natureOfBusinessActivity?.message}
                className="borderless"
                tabIndex={
                  !activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                  ) ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                  ) ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
              />
            )}
          />
        </InfoRow>
      )}
      <InfoRow title="Position" id="employeePosition" lessPadding>
        <Controller
          control={control}
          name="employeePosition"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="employeePosition"
              placeholder="Enter position"
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['employeePosition']);
              }}
              size={InputSize.SMALL}
              error={errors?.employeePosition?.message}
              className="borderless"
              tabIndex={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          )}
        />
      </InfoRow>
      <InfoRow
        title="Country of Employment"
        id="countryOfEmployerId"
        lessPadding
      >
        <Controller
          control={control}
          name="countryOfEmployerId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              name="countryOfEmployerId"
              placeholder="Select country..."
              list={countries || []}
              value={value}
              className={`${cn.newApplicationAutocomplete} ${
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.newApplicationAutocompleteDisabled
                  : ''
              }`}
              error={errors?.countryOfEmployerId?.message}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['countryOfEmployerId']);
              }}
              borderless
              tabIndex={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              fullWidth
            />
          )}
        />
      </InfoRow>
    </>
  );

  return (
    <div className={cn.stepBlockWrapper} style={{ zIndex: 20 }}>
      <div className={cn.stepBlockContent}>
        <StepPartBlock title="General information" zIndex={30}>
          <InfoRow title="First Name" id="firstName" lessPadding>
            <Controller
              control={control}
              name="firstName"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <InputText
                  name="firstName"
                  placeholder="Enter first name..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['firstName']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  error={errors?.firstName?.message}
                  tabIndex={
                    hasSumSubId ||
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    hasSumSubId ||
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
          <InfoRow title="Last name" id="lastName" lessPadding>
            <Controller
              control={control}
              name="lastName"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <InputText
                  name="lastName"
                  placeholder="Enter last name..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['lastName']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  error={errors?.lastName?.message}
                  tabIndex={
                    hasSumSubId ||
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    hasSumSubId ||
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
          <InfoRow title="Date of birth" id="dob" lessPadding>
            <Controller
              control={control}
              name="dob"
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                let className;

                if (
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED
                ) {
                  className = cn.dateInputDisabled;

                  if (value) {
                    className = `${cn.dateInputDisabled} ${
                      value ? cn.dateInputDisabledWithValue : ''
                    }`;
                  }
                }
                return (
                  <DateInput
                    value={value || ''}
                    placeholder="Enter date of birth"
                    onChange={(date) => {
                      onChange(date.toISOString());
                      clearErrors(['dob']);
                    }}
                    className={className}
                    size={DateInputSize.SMALL}
                    error={errors?.dob?.message}
                    tabIndex={
                      hasSumSubId ||
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      hasSumSubId ||
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    birthdayPicker
                    borderless
                    withoutCustomSelector
                  />
                );
              }}
            />
          </InfoRow>

          {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
            <InfoRow
              title="Place of birth"
              id="birthCountryId"
              style={{
                position: 'relative',
                zIndex: 19,
              }}
              lessPadding
            >
              <div className={cn.placeOfBirthWrapper}>
                <div className={cn.placeOfBirthRow}>
                  <Controller
                    control={control}
                    name="birthCountryId"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Autocomplete
                        name={name}
                        placeholder="Select country..."
                        list={countries || []}
                        value={value}
                        className={`${cn.newApplicationAutocomplete} ${
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? cn.newApplicationAutocompleteDisabled
                            : ''
                        }`}
                        error={errors?.birthCountryId?.message}
                        size={SelectSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['birthCountryId']);
                        }}
                        borderless
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        fullWidth
                      />
                    )}
                  />
                </div>
                <div className={cn.placeOfBirthRow}>
                  <Controller
                    control={control}
                    name="placeOfBirth"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter city.."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['placeOfBirth']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        error={errors?.placeOfBirth?.message}
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </div>
              </div>
            </InfoRow>
          )}
          <InfoRow
            title="Nationality"
            id="nationalityId"
            className={cn.nationalityWrapper}
            style={{
              position: 'relative',
              zIndex: 18,
            }}
            lessPadding
          >
            <Controller
              control={control}
              name="nationalityId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  name="nationalityId"
                  placeholder="Select country..."
                  list={countries || []}
                  value={value}
                  className={`${cn.newApplicationAutocomplete} ${
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.newApplicationAutocompleteDisabled
                      : ''
                  }`}
                  error={errors?.nationalityId?.message}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['nationalityId']);
                  }}
                  borderless
                  tabIndex={
                    hasSumSubId ||
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
          </InfoRow>
          {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
            <>
              <InfoRow
                title="Other nationalities"
                id="otherNationalitiesIds"
                className={cn.nationalityWrapper}
                style={{
                  position: 'relative',
                  zIndex: 17,
                }}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="otherNationalitiesIds"
                  defaultValue={[]}
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select countries..."
                      list={countries || []}
                      value={value}
                      className={`${cn.newApplicationAutocomplete} ${
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? cn.newApplicationAutocompleteDisabled
                          : ''
                      }`}
                      error={
                        questionnarieErrors?.otherNationalitiesIds?.message
                      }
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['otherNationalitiesIds']);

                        if (!(e.target.value as string[])?.length) {
                          questionnarieClearErrors([
                            'secondPassportIssueCountryId',
                          ]);
                          questionnarieClearErrors(['secondPassportIssueDate']);
                          questionnarieClearErrors(['secondPassportNumber']);
                        }
                      }}
                      borderless
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      mode={SelectType.MULTIPLE}
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Marital Status"
                id="maritalStatus"
                className={cn.nationalityWrapper}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="maritalStatus"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select status..."
                      list={[
                        {
                          name: 'Single',
                          value: 'SINGLE',
                        },
                        {
                          name: 'Divorced',
                          value: 'DIVORCED',
                        },
                        {
                          name: 'Married',
                          value: 'MARRIED',
                        },
                      ]}
                      value={value}
                      className={`${cn.newApplicationAutocomplete} ${
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? cn.newApplicationAutocompleteDisabled
                          : ''
                      }`}
                      error={questionnarieErrors?.maritalStatus?.message}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['maritalStatus']);
                      }}
                      borderless
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              {questionnarieFields?.maritalStatus === 'MARRIED' && (
                <InfoRow
                  title="Spouse full name"
                  id="spouseFullName"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="spouseFullName"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter full name.."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['spouseFullName']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        error={questionnarieErrors?.spouseFullName?.message}
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
            </>
          )}

          {applicationScheme && applicationScheme?.taxNumberActive && (
            <InfoRow title="TAX Number" id="taxNumber" lessPadding>
              <Controller
                control={control}
                name="taxNumber"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="taxNumber"
                    placeholder="Enter TAX..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['taxNumber']);
                    }}
                    size={InputSize.SMALL}
                    error={errors?.taxNumber?.message}
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          )}
        </StepPartBlock>
        <StepPartBlock title="Contacts" zIndex={29}>
          <InfoRow title="Phone number" id="phone" lessPadding>
            <Controller
              control={control}
              name="phone"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <InputText
                  name="phone"
                  placeholder="Enter phone number..."
                  value={value ? formatPhone(value) : ''}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['phone']);
                  }}
                  size={InputSize.SMALL}
                  error={errors?.phone?.message}
                  className="borderless"
                  labelClassName={cn.newApplicationInputLabel}
                  tabIndex={-1}
                  disabled
                />
              )}
            />
          </InfoRow>
          <InfoRow title="E-mail (optional)" id="email" lessPadding>
            <Controller
              control={control}
              name="email"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <InputText
                  name="email"
                  placeholder="Enter email..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['email']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  error={errors?.email?.message}
                  tabIndex={
                    hasSumSubId ||
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    hasSumSubId ||
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        </StepPartBlock>
        {regulatedCompanyScheme && (
          <StepPartBlock title="Additional Contacts">
            <InfoRow title="Home phone number" id="homePhoneNumber" lessPadding>
              <Controller
                control={questionnarieControl}
                name="homePhoneNumber"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="homePhoneNumber"
                    placeholder="Enter Phone number..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['homePhoneNumber']);
                    }}
                    size={InputSize.SMALL}
                    error={questionnarieErrors?.homePhoneNumber?.message}
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
            {additionalEmails?.map((item, index) => (
              <InfoRow
                title={`Email #${index + 1}`}
                id="emails"
                key={item.id}
                lessPadding
              >
                <div className={cn.additionalEmailWrapper}>
                  <InputText
                    name={`email-${item.id}`}
                    placeholder="Enter email..."
                    value={item.email}
                    onChange={(e) => {
                      onChangeEmail({
                        target: {
                          name: `email-${item.id}`,
                          value: e.target.value,
                        },
                      } as ChangeEvent<HTMLInputElement>);
                      questionnarieClearErrors(['emails']);
                    }}
                    size={InputSize.SMALL}
                    className="borderless"
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                  {index > 0 &&
                    status !== NewApplicationStatus.ACCEPTED &&
                    status !== NewApplicationStatus.DECLINED && (
                      <button
                        type="button"
                        className={cn.removeEmailButton}
                        onClick={() => onDeleteAdditionalEmails(item.id)}
                      >
                        <IconClose width={16} height={16} />
                      </button>
                    )}
                </div>
              </InfoRow>
            ))}
            <div className={cn.addSourceButtonWrapper}>
              <ButtonSecondary
                text="Add email"
                icon={<IconExpand width={16} height={16} />}
                size={ButtonSize.SMALL}
                className={`button-white ${cn.addSourceButton}`}
                onClick={() => {
                  const nextEmails = (
                    [] as {
                      id: string;
                      email: string;
                    }[]
                  ).concat(additionalEmails);

                  nextEmails.push({
                    id: `email-${uuid()}`,
                    email: '',
                  });

                  setAdditionalEmails(nextEmails);
                }}
              />
            </div>
            {questionnarieErrors?.emails?.message && (
              <div className={cn.stepOneQuestionnaireError}>
                {questionnarieErrors?.emails?.message}
              </div>
            )}
            <Controller
              control={questionnarieControl}
              name="emails"
              render={({ field: { name } }) => (
                <input type="hidden" name={name} />
              )}
            />
          </StepPartBlock>
        )}
        {applicationScheme &&
          (applicationScheme?.civilActive ||
            applicationScheme?.criminalOfAnyActive ||
            applicationScheme?.lastThreeYearsAddressHistoryActive ||
            applicationScheme?.nationalInsuranceNumberOfDirectorsActive ||
            applicationScheme?.regulatoryActive ||
            applicationScheme?.businessAndEmploymentActive) && (
            <StepPartBlock title="Additional Info" zIndex={28}>
              {applicationScheme?.lastThreeYearsAddressHistoryActive && (
                <InfoRow
                  title="National Insurance number of directors"
                  id="nationalInsuranceNumberOfDirectors"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="nationalInsuranceNumberOfDirectors"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <InputText
                        name="nationalInsuranceNumberOfDirectors"
                        placeholder="Enter count..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors([
                            'nationalInsuranceNumberOfDirectors',
                          ]);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        error={
                          questionnarieErrors
                            ?.nationalInsuranceNumberOfDirectors?.message
                        }
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED
                            ? -1
                            : 0
                        }
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              {applicationScheme?.lastThreeYearsAddressHistoryActive && (
                <InfoRow
                  title="Last 3 years address history"
                  id="lastThreeYearsAddressHistory"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="lastThreeYearsAddressHistory"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Textarea
                        name="lastThreeYearsAddressHistory"
                        placeholder="list the addresses..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors([
                            'lastThreeYearsAddressHistory',
                          ]);
                        }}
                        className={`borderless ${cn.stepOneTextarea} ${cn.pepTextarea}`}
                        error={
                          questionnarieErrors?.lastThreeYearsAddressHistory
                            ?.message
                        }
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED
                            ? -1
                            : 0
                        }
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              {applicationScheme?.criminalOfAnyActive && (
                <StepOneAdditionalBlock
                  title="Criminal Proceedings"
                  defaultOpen
                >
                  <>
                    <InfoRow
                      title="Have you ever been convicted of any criminal offence?"
                      id="criminalReceivedACautionForAny"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="criminalReceivedACautionForAny"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="criminalReceivedACautionForAny"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'criminalReceivedACautionForAny',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.criminalReceivedACautionForAny?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever received a caution for any criminal offence?"
                      id="criminalConvictedOfAny"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="criminalConvictedOfAny"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="criminalConvictedOfAny"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'criminalConvictedOfAny',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors?.criminalConvictedOfAny
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been arrested or charged with any criminal offence or been involved in a criminal investigation?"
                      id="criminalArrestedOrChargedWithOrBeenInvolved"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="criminalArrestedOrChargedWithOrBeenInvolved"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="criminalArrestedOrChargedWithOrBeenInvolved"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'criminalArrestedOrChargedWithOrBeenInvolved',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.criminalArrestedOrChargedWithOrBeenInvolved
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been subject to any current criminal investigation?"
                      id="criminalSubjectToAnyCurrentCriminalInvestigation"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="criminalSubjectToAnyCurrentCriminalInvestigation"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="criminalSubjectToAnyCurrentCriminalInvestigation"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'criminalSubjectToAnyCurrentCriminalInvestigation',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.criminalSubjectToAnyCurrentCriminalInvestigation
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Has any firm where you held a position of influence ever been convicted, charged, or investigated for a criminal offence?"
                      id="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Has any firm where you held a position of influence ever been ordered to produce documents for or been searched in relation to a criminal investigation?"
                      id="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Has any firm where you held a position of influence ever been involved in current criminal proceedings or investigations?"
                      id="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                  </>
                </StepOneAdditionalBlock>
              )}
              {applicationScheme?.civilActive && (
                <StepOneAdditionalBlock title="Civil Proceedings" defaultOpen>
                  <>
                    <InfoRow
                      title="Have you ever been found liable for fraud, negligence, or misconduct by a court?"
                      id="civilFoundLiableForFraud"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="civilFoundLiableForFraud"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="civilFoundLiableForFraud"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'civilFoundLiableForFraud',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors?.civilFoundLiableForFraud
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been subject to a judgment debt or award?"
                      id="civilSubjectToAJudgmentDebt"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="civilSubjectToAJudgmentDebt"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="civilSubjectToAJudgmentDebt"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'civilSubjectToAJudgmentDebt',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors?.civilSubjectToAJudgmentDebt
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been involved in any civil proceedings that resulted in a finding against you?"
                      id="civilInvolvedInAnyCivilProceedings"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="civilInvolvedInAnyCivilProceedings"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="civilInvolvedInAnyCivilProceedings"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'civilInvolvedInAnyCivilProceedings',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.civilInvolvedInAnyCivilProceedings?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Has any firm where you held a position of influence ever been found liable in civil proceedings for fraud or misconduct?"
                      id="civilYouHeldAPositionOfInfluence"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="civilYouHeldAPositionOfInfluence"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="civilYouHeldAPositionOfInfluence"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'civilYouHeldAPositionOfInfluence',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.civilYouHeldAPositionOfInfluence?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever filed for bankruptcy or been judged bankrupt?"
                      id="civilFiledForBankruptcy"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="civilFiledForBankruptcy"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="civilFiledForBankruptcy"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'civilFiledForBankruptcy',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors?.civilFiledForBankruptcy
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been subject to a bankruptcy restriction order or made any arrangements with creditors?"
                      id="civilSubjectToABankruptcy"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="civilSubjectToABankruptcy"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="civilSubjectToABankruptcy"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'civilSubjectToABankruptcy',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors?.civilSubjectToABankruptcy
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                  </>
                </StepOneAdditionalBlock>
              )}
              {applicationScheme?.businessAndEmploymentActive && (
                <StepOneAdditionalBlock
                  title="Business and Employment Matters"
                  defaultOpen
                >
                  <>
                    <InfoRow
                      title="Have you ever been dismissed or asked to resign from a position?"
                      id="businessAndEmploymentDismissedOrAskedTo"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="businessAndEmploymentDismissedOrAskedTo"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="businessAndEmploymentDismissedOrAskedTo"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'businessAndEmploymentDismissedOrAskedTo',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.businessAndEmploymentDismissedOrAskedTo
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been disqualified from acting as a director?"
                      id="businessAndEmploymentDisqualifiedFromActingAsADirector"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="businessAndEmploymentDisqualifiedFromActingAsADirector"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="businessAndEmploymentDisqualifiedFromActingAsADirector"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'businessAndEmploymentDisqualifiedFromActingAsADirector',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.businessAndEmploymentDisqualifiedFromActingAsADirector
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been the subject of any disciplinary proceedings or investigations?"
                      id="businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                  </>
                </StepOneAdditionalBlock>
              )}
              {applicationScheme?.regulatoryActive && (
                <StepOneAdditionalBlock title="Regulatory Matters" defaultOpen>
                  <>
                    <InfoRow
                      title="Have you ever had an authorization from a regulatory body refused, revoked, or terminated?"
                      id="regulatoryAnAuthorizationFromARegulatoryBodyRefused"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="regulatoryAnAuthorizationFromARegulatoryBodyRefused"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="regulatoryAnAuthorizationFromARegulatoryBodyRefused"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'regulatoryAnAuthorizationFromARegulatoryBodyRefused',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.regulatoryAnAuthorizationFromARegulatoryBodyRefused
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been censured, disciplined, or investigated by a regulatory body?"
                      id="regulatoryCensured"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="regulatoryCensured"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="regulatoryCensured"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors(['regulatoryCensured']);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors?.regulatoryCensured?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been involved in any activities requiring authorization that you did not have?"
                      id="regulatoryInvolvedInAnyActivitiesRequiringAuthorization"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="regulatoryInvolvedInAnyActivitiesRequiringAuthorization"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="regulatoryInvolvedInAnyActivitiesRequiringAuthorization"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'regulatoryInvolvedInAnyActivitiesRequiringAuthorization',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                    <InfoRow
                      title="Have you ever been subject to any disqualification or prohibition orders under financial regulations?"
                      id="regulatorySubjectToAnyDisqualification"
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="regulatorySubjectToAnyDisqualification"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="regulatorySubjectToAnyDisqualification"
                            placeholder="Select «Yes» or «No»"
                            list={[
                              {
                                name: 'Yes',
                                value: 'yes',
                              },
                              {
                                name: 'No',
                                value: 'no',
                              },
                            ]}
                            value={value as string}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors([
                                'regulatorySubjectToAnyDisqualification',
                              ]);
                            }}
                            className="borderless"
                            size={SelectSize.SMALL}
                            error={
                              questionnarieErrors
                                ?.regulatorySubjectToAnyDisqualification
                                ?.message
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? -1
                                : 0
                            }
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            fullWidth
                          />
                        )}
                      />
                    </InfoRow>
                  </>
                </StepOneAdditionalBlock>
              )}
            </StepPartBlock>
          )}
        {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
          <>
            <StepPartBlock title="Professional information" zIndex={28}>
              <InfoRow
                title="Activity of the client"
                id="clientActivityId"
                lessPadding
              >
                <Controller
                  control={control}
                  name="clientActivityId"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="clientActivityId"
                      placeholder="Select an option"
                      list={clientsActivities}
                      value={value}
                      selectWrapperClassName={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.selectDisabled
                          : ''
                      }
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['clientActivityId']);
                      }}
                      error={errors?.clientActivityId?.message}
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              {employmentActivity?.value === clientActivity &&
                employmentContent}
              {clientsActivities?.find((item) => item.name === 'Self-employed')
                ?.value === clientActivity && (
                <InfoRow
                  title="Nature of business activity"
                  id="natureOfBusinessActivity"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="natureOfBusinessActivity"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter description..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors([
                            'natureOfBusinessActivity',
                          ]);
                        }}
                        size={InputSize.SMALL}
                        error={
                          questionnarieErrors?.natureOfBusinessActivity?.message
                        }
                        className="borderless"
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              {otherActivity?.value === clientActivity && (
                <InfoRow
                  title="Description of activity"
                  id="typeOfActivityDescription"
                  className={cn.noFlex}
                  lessPadding
                >
                  <Controller
                    control={control}
                    name="typeOfActivityDescription"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Textarea
                        name="typeOfActivityDescription"
                        placeholder="Enter description..."
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['typeOfActivityDescription']);
                        }}
                        value={value}
                        className={`borderless ${cn.stepOneTextarea} ${cn.pepTextarea}`}
                        error={errors?.typeOfActivityDescription?.message}
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              {businessOwnerActivity?.value === clientActivity && (
                <>
                  <InfoRow
                    title="Name of the Company"
                    id="nameOfCompany"
                    lessPadding
                  >
                    <Controller
                      control={questionnarieControl}
                      name="nameOfCompany"
                      defaultValue=""
                      render={({ field: { onChange, value, name } }) => (
                        <InputText
                          name={name}
                          placeholder="Enter name..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            questionnarieClearErrors(['nameOfCompany']);
                          }}
                          size={InputSize.SMALL}
                          error={questionnarieErrors?.nameOfCompany?.message}
                          className="borderless"
                          tabIndex={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          disabled={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow title="Ownership in %" id="ownership" lessPadding>
                    <Controller
                      control={questionnarieControl}
                      name="ownership"
                      defaultValue=""
                      render={({ field: { onChange, value, name } }) => (
                        <InputNumber
                          name={name}
                          placeholder="Enter percent..."
                          value={value}
                          onChange={(e) => {
                            let ownershipValue = e.target.value;

                            if (Number(e.target.value) > 100) {
                              ownershipValue = '100';
                            } else if (Number(e.target.value) < 0) {
                              ownershipValue = '0';
                            }

                            onChange(ownershipValue);
                            questionnarieClearErrors(['ownership']);
                          }}
                          size={InputSize.SMALL}
                          error={questionnarieErrors?.ownership?.message}
                          className="borderless"
                          tabIndex={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          disabled={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow
                    title="Country of Employment"
                    id="countryOfEmployerId"
                    lessPadding
                  >
                    <Controller
                      control={control}
                      name="countryOfEmployerId"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          name="countryOfEmployerId"
                          placeholder="Select country..."
                          list={countries || []}
                          value={value}
                          className={`${cn.newApplicationAutocomplete} ${
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED
                              ? cn.newApplicationAutocompleteDisabled
                              : ''
                          }`}
                          error={errors?.countryOfEmployerId?.message}
                          size={SelectSize.SMALL}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['countryOfEmployerId']);
                          }}
                          borderless
                          tabIndex={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          disabled={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          fullWidth
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow
                    title="Nature of business activity"
                    id="natureOfBusinessActivity"
                    lessPadding
                  >
                    <Controller
                      control={questionnarieControl}
                      name="natureOfBusinessActivity"
                      defaultValue=""
                      render={({ field: { onChange, value, name } }) => (
                        <InputText
                          name={name}
                          placeholder="Enter description..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            questionnarieClearErrors([
                              'natureOfBusinessActivity',
                            ]);
                          }}
                          size={InputSize.SMALL}
                          error={
                            questionnarieErrors?.natureOfBusinessActivity
                              ?.message
                          }
                          className="borderless"
                          tabIndex={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          disabled={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                        />
                      )}
                    />
                  </InfoRow>
                </>
              )}
            </StepPartBlock>
            {cofRegulatedCompanyScheme &&
              setAffilatedCompaniesValues &&
              onDeleteAffilatedCompany && (
                <AffilatedCompanies
                  questionnarieControl={questionnarieControl}
                  questionnarieClearErrors={questionnarieClearErrors}
                  questionnarieErrors={questionnarieErrors}
                  status={status}
                  isEditable={activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                  )}
                  questionnarieFields={questionnarieFields}
                  affilatedCompaniesValues={affilatedCompaniesValues}
                  setAffilatedCompaniesValues={setAffilatedCompaniesValues}
                  affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
                  affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
                  deleteAffilatedCompanyInProgress={
                    deleteAffilatedCompanyInProgress
                  }
                  onDeleteAffilatedCompany={onDeleteAffilatedCompany}
                  selectedAffilatedCompanyId={selectedAffilatedCompanyId}
                  uboPercentage={uboPercentage}
                  countries={countries}
                />
              )}
            <GroupInformation
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              status={status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              countries={countries}
              sourceOfWealth={sourceOfWealth}
              baseCurrency={baseCurrency}
              questionnarieSetValue={questionnarieSetValue}
              questionnarieFields={questionnarieFields}
              groupStructurChartFile={groupStructurChartFile}
              isCof={companyOfficial}
            />
            <SourceOfIncome
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              status={status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              sourceOfIncome={sourceOfIncome}
              questionnarieSetValue={questionnarieSetValue}
              questionnarieFields={questionnarieFields}
              baseCurrency={baseCurrency}
            />
            <SourceAndSizeOfWealth
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              status={status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              questionnarieSetValue={questionnarieSetValue}
              questionnarieFields={questionnarieFields}
              baseCurrency={baseCurrency}
            />
            <StepPartBlock title="Net worth" zIndex={23}>
              <InfoRow
                title="Require"
                id="requiresNetWorth"
                style={{
                  position: 'relative',
                  zIndex: 17,
                }}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="requiresNetWorth"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select an option..."
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['requiresNetWorth']);

                        if (e.target.value === 'no') {
                          questionnarieClearErrors(['netWorthAmount']);
                          questionnarieSetValue('netWorthAmount', '');
                        }
                      }}
                      selectWrapperClassName={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.selectDisabled
                          : ''
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={questionnarieErrors?.requiresNetWorth?.message}
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              {questionnarieFields?.requiresNetWorth === 'yes' && (
                <InfoRow
                  title="Net Worth"
                  id="netWorthAmount"
                  style={{
                    position: 'relative',
                    zIndex: 16,
                  }}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="netWorthAmount"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter amount..."
                        value={value as string}
                        size={InputSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['netWorthAmount']);
                        }}
                        className="borderless"
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !activePermissions?.includes(
                            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                          ) &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={questionnarieErrors?.netWorthAmount?.message}
                        currency={baseCurrency}
                        amountInput
                      />
                    )}
                  />
                </InfoRow>
              )}
            </StepPartBlock>
          </>
        )}
        {!companyOfficial && !regulatedCompanyScheme && (
          <StepPartBlock title="Activities" zIndex={23}>
            <InfoRow
              title="Source of Funds"
              id="sourceOfFundsIds"
              className={cn.sourceOfFundsWrapper}
              lessPadding
            >
              <Controller
                control={control}
                name="sourceOfFundsIds"
                defaultValue={[]}
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="sourceOfFundsIds"
                    placeholder="Select an option"
                    list={sourcesOfFunds}
                    value={value}
                    selectWrapperClassName={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? cn.selectDisabled
                        : ''
                    }
                    size={SelectSize.SMALL}
                    mode={SelectType.MULTIPLE}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['sourceOfFundsIds', 'sourcesOfFunds']);
                    }}
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    error={
                      errors?.sourceOfFundsIds?.message ||
                      errors?.sourcesOfFunds?.message
                    }
                    fullWidth
                    borderless
                  />
                )}
              />
            </InfoRow>
            {sourceOfFundsIds?.includes(
              otherSourceOfFunds?.value as string,
            ) && (
              <InfoRow
                title="Description of source of funds"
                id="sourceOfFundsDescription"
                lessPadding
              >
                <Controller
                  control={control}
                  name="sourceOfFundsDescription"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="sourceOfFundsDescription"
                      placeholder="Enter description..."
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['sourceOfFundsDescription']);
                      }}
                      value={value}
                      className={`borderless ${cn.stepOneTextarea} ${cn.pepTextarea}`}
                      error={errors?.sourceOfFundsDescription?.message}
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
            <InfoRow
              title="Activity of the client"
              id="clientActivityId"
              lessPadding
            >
              <Controller
                control={control}
                name="clientActivityId"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="clientActivityId"
                    placeholder="Select an option"
                    list={clientsActivities?.filter(
                      (item) => item.name !== 'Business Owner',
                    )}
                    value={value}
                    selectWrapperClassName={
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? cn.selectDisabled
                        : ''
                    }
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['clientActivityId']);
                    }}
                    error={errors?.clientActivityId?.message}
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    fullWidth
                    borderless
                  />
                )}
              />
            </InfoRow>
            {employmentActivity?.value === clientActivity && employmentContent}
            {otherActivity?.value === clientActivity && (
              <InfoRow
                title="Description of activity"
                id="typeOfActivityDescription"
                lessPadding
              >
                <Controller
                  control={control}
                  name="typeOfActivityDescription"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="typeOfActivityDescription"
                      placeholder="Enter description..."
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['typeOfActivityDescription']);
                      }}
                      value={value}
                      className={`borderless ${cn.stepOneTextarea} ${cn.pepTextarea}`}
                      error={errors?.typeOfActivityDescription?.message}
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
          </StepPartBlock>
        )}
        {(!applicationScheme || applicationScheme.deliveryChannelVisible) && (
          <StepPartBlock zIndex={22} withoutTitle>
            <InfoRow
              title={
                applicationScheme && applicationScheme?.deliveryChannelTitle
                  ? applicationScheme?.deliveryChannelTitle
                  : 'Delivery channel'
              }
              id="deliveryChannelId"
              lessPadding
            >
              <Controller
                control={control}
                name="deliveryChannelId"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="deliveryChannelId"
                    placeholder="Select an option..."
                    list={deliveryChannelsIndividual}
                    value={value}
                    error={errors?.deliveryChannelId?.message}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['deliveryChannelId']);
                    }}
                    selectWrapperClassName={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? cn.selectDisabled
                        : ''
                    }
                    borderless
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    fullWidth
                  />
                )}
              />
            </InfoRow>
          </StepPartBlock>
        )}
      </div>

      <Controller
        control={control}
        name="userId"
        defaultValue=""
        render={({ field: { value } }) => (
          <input type="hidden" name="userId" value={value} tabIndex={-1} />
        )}
      />
    </div>
  );
};

export default NewApplicationInfoStepOne;
